/**
 * Case Strength Indicator Component
 * 
 * Displays the automation-calculated case strength score with visual indicators.
 * Part of the 90% Automation System visibility UI.
 */

import React from 'react';

interface CaseStrengthIndicatorProps {
  score: number;  // 0-100
  isReady: boolean;
  isAutoReady?: boolean;
  blocking?: number;
  compact?: boolean;
  showDetails?: boolean;
}

export const CaseStrengthIndicator: React.FC<CaseStrengthIndicatorProps> = ({
  score,
  isReady,
  isAutoReady = false,
  blocking = 0,
  compact = false,
  showDetails = false
}) => {
  // Color based on score
  const getScoreColor = () => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getBackgroundColor = () => {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 70) return 'bg-blue-50 border-blue-200';
    if (score >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getProgressColor = () => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-blue-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusText = () => {
    if (isAutoReady) return 'Auto-Ready';
    if (isReady) return 'Ready';
    if (blocking > 0) return `${blocking} Blocking`;
    if (score >= 70) return 'In Progress';
    return 'Needs Work';
  };

  const getStatusBadgeColor = () => {
    if (isAutoReady) return 'bg-green-100 text-green-800';
    if (isReady) return 'bg-green-100 text-green-700';
    if (blocking > 0) return 'bg-red-100 text-red-700';
    if (score >= 70) return 'bg-blue-100 text-blue-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className={`text-lg font-bold ${getScoreColor()}`}>
          {score}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeColor()}`}>
          {getStatusText()}
        </span>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-4 ${getBackgroundColor()}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="font-medium text-gray-700">Case Strength</span>
        </div>
        <span className={`text-sm px-2.5 py-1 rounded-full font-medium ${getStatusBadgeColor()}`}>
          {getStatusText()}
        </span>
      </div>

      {/* Score Display */}
      <div className="flex items-center gap-4">
        <div className={`text-4xl font-bold ${getScoreColor()}`}>
          {score}
        </div>
        <div className="flex-1">
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-1">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor()}`}
              style={{ width: `${score}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>0</span>
            <span className="text-gray-400">Ready: 90</span>
            <span>100</span>
          </div>
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="text-center">
              <div className="text-gray-500">Blocking</div>
              <div className={`font-bold ${blocking > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {blocking}
              </div>
            </div>
            <div className="text-center">
              <div className="text-gray-500">Ready</div>
              <div className={`font-bold ${isReady ? 'text-green-600' : 'text-gray-400'}`}>
                {isReady ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-gray-500">Auto-Ready</div>
              <div className={`font-bold ${isAutoReady ? 'text-green-600' : 'text-gray-400'}`}>
                {isAutoReady ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStrengthIndicator;
